// Editor/visor reutilitzable de la part de "Períodes / Rangs / Trams" d'un
// Horari (Empresa o Empleat). Es fa servir tal qual des de HorariEmpresa.js
// i, en mode readOnly, també des de Detallsempleat/Consultarempleat per
// mostrar l'Horari vigent d'un Empleat sense exposar la seva estructura
// tècnica (Períodes/Rangs/Trams) com a text pla.
//
// Els endpoints que crida (NouPeriodeHorari.php, ModificarPeriodeHorari.php,
// EsborrarPeriodeHorari.php, NouRangPeriode.php, ModificarRangPeriode.php,
// EsborrarRangPeriode.php) validen l'Horari contra l'Empresa activa i estan
// protegits per GestioLaboralAuth (SUPER/ADMIN), igual que a l'Horari
// d'Empresa: gestionar Horaris (d'Empresa o d'Empleat) queda dins del
// mateix perímetre de seguretat de "Gestió laboral".

import React, { useState } from "react";
import { fetchAutenticat, SessioCaducadaError } from "../../utils/auth";
import { formatDate } from "../../utils/Utilitats";
import TeclaEscEnter from "../../utils/Teclaescenter";
import { API_URL } from "../../utils/config";

export const DIES_SETMANA = [
  { valor: 1, nom: "Dilluns", curt: "Dl" },
  { valor: 2, nom: "Dimarts", curt: "Dt" },
  { valor: 3, nom: "Dimecres", curt: "Dc" },
  { valor: 4, nom: "Dijous", curt: "Dj" },
  { valor: 5, nom: "Divendres", curt: "Dv" },
  { valor: 6, nom: "Dissabte", curt: "Ds" },
  { valor: 7, nom: "Diumenge", curt: "Dg" },
];

export const NOMS_MESOS = [
  "Gener", "Febrer", "Març", "Abril", "Maig", "Juny",
  "Juliol", "Agost", "Setembre", "Octubre", "Novembre", "Desembre",
];

export function formatDataISO(any, mes, dia) {
  const mm = String(mes + 1).padStart(2, "0");
  const dd = String(dia).padStart(2, "0");
  return `${any}-${mm}-${dd}`;
}

// Genera les cel·les d'un mes (null = cel·la buida abans del dia 1), amb la setmana començant en dilluns.
export function generarDiesMes(any, mes) {
  const primerDiaSetmana = (new Date(any, mes, 1).getDay() + 6) % 7;
  const numDies = new Date(any, mes + 1, 0).getDate();
  const dies = [];
  for (let i = 0; i < primerDiaSetmana; i++) dies.push(null);
  for (let d = 1; d <= numDies; d++) dies.push(d);
  return dies;
}

// "09:30:00" -> "9:30h"; "14:00:00" -> "14h". Traiem segons i minuts quan són 0.
export function formatHoraCurta(hora) {
  if (!hora) return "";
  const parts = String(hora).split(":");
  const h = parseInt(parts[0], 10) || 0;
  const m = parseInt(parts[1], 10) || 0;
  const s = parseInt(parts[2], 10) || 0;
  if (s !== 0) return `${h}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}h`;
  if (m !== 0) return `${h}:${String(m).padStart(2, "0")}h`;
  return `${h}h`;
}

// Uneix frases amb comes i un "i" abans de l'última: "A i B" / "A, B, i C".
export function unirAmbI(frases) {
  if (frases.length === 0) return "";
  if (frases.length === 1) return frases[0];
  if (frases.length === 2) return `${frases[0]} i ${frases[1]}`;
  return `${frases.slice(0, -1).join(", ")}, i ${frases[frases.length - 1]}`;
}

// Franges d'un dia -> "De 9h a 14h" / "De 9:30h a 14:30h i de 15h a 18h" / "Tancat".
export function formatTramsDia(tramsDia) {
  if (!tramsDia || tramsDia.length === 0) return "Tancat";
  const frases = tramsDia.map((t) => `de ${formatHoraCurta(t.HoraInici)} a ${formatHoraCurta(t.HoraFinal)}`);
  frases[0] = "De" + frases[0].slice(2);
  return unirAmbI(frases);
}

// Calendari estil "aerolínia": mostra l'any complet (12 mesos) perquè els
// períodes solen abastar diversos mesos. Primer clic marca la data d'inici,
// segon clic la data final. Esc cancel·la, Enter confirma si el rang és vàlid.
export function SelectorRangDates({ dataIniciInicial, dataFinalInicial, onConfirmar, onCancelar }) {
  const base = dataIniciInicial ? new Date(dataIniciInicial) : new Date();
  const [anyVisible, setAnyVisible] = useState(base.getFullYear());
  const [inici, setInici] = useState(dataIniciInicial || null);
  const [final, setFinal] = useState(dataFinalInicial || null);
  const [hover, setHover] = useState(null);

  const handleConfirmar = () => {
    if (inici && final) onConfirmar(inici, final);
  };

  TeclaEscEnter(onCancelar, handleConfirmar);

  const handleClickDia = (dataISO) => {
    if (!inici || final) {
      setInici(dataISO);
      setFinal(null);
    } else if (dataISO < inici) {
      setInici(dataISO);
      setFinal(null);
    } else {
      setFinal(dataISO);
    }
  };

  const previewFinal = inici && !final && hover && hover >= inici ? hover : null;
  const finalEfectiu = final || previewFinal;

  return (
    <div className="horari-empresa-calendari">
      <div className="horari-empresa-calendari-capcalera">
        <button type="button" className="dv2-btn dv2-btn--icon" onClick={() => setAnyVisible((a) => a - 1)}>‹</button>
        <span className="horari-empresa-calendari-any">{anyVisible}</span>
        <button type="button" className="dv2-btn dv2-btn--icon" onClick={() => setAnyVisible((a) => a + 1)}>›</button>
      </div>

      <div className="horari-empresa-calendari-mesos" onMouseLeave={() => setHover(null)}>
        {NOMS_MESOS.map((nomMes, mes) => (
          <div className="horari-empresa-calendari-mes" key={mes}>
            <div className="horari-empresa-calendari-mes-titol">{nomMes}</div>
            <div className="horari-empresa-calendari-dies-setmana">
              {DIES_SETMANA.map((d) => (
                <div key={d.valor} className="horari-empresa-calendari-dia-setmana">{d.curt}</div>
              ))}
            </div>
            <div className="horari-empresa-calendari-graella">
              {generarDiesMes(anyVisible, mes).map((dia, index) => {
                if (dia === null) {
                  return <div key={index} className="horari-empresa-calendari-dia horari-empresa-calendari-dia-buit" />;
                }
                const dataISO = formatDataISO(anyVisible, mes, dia);
                const esExtrem = dataISO === inici || dataISO === final;
                const enRang = inici && finalEfectiu && dataISO > inici && dataISO < finalEfectiu;
                const classes = ["horari-empresa-calendari-dia"];
                if (esExtrem) classes.push("horari-empresa-calendari-dia-extrem");
                if (enRang) classes.push("horari-empresa-calendari-dia-rang");
                return (
                  <div
                    key={index}
                    className={classes.join(" ")}
                    onClick={() => handleClickDia(dataISO)}
                    onMouseEnter={() => setHover(dataISO)}
                  >
                    {dia}
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      <div className="horari-empresa-calendari-resum">
        {inici && final ? (
          <span>Del <strong>{formatDate(inici)}</strong> al <strong>{formatDate(final)}</strong></span>
        ) : inici ? (
          <span>Inici: <strong>{formatDate(inici)}</strong> — selecciona ara la data final</span>
        ) : (
          <span>Selecciona la data d'inici</span>
        )}
      </div>

      <div className="horari-empresa-accions">
        <button
          type="button"
          className="dv2-btn dv2-btn--primary"
          disabled={!inici || !final}
          onClick={handleConfirmar}
        >
          Confirmar dates
        </button>
        <button type="button" className="dv2-btn" onClick={onCancelar}>
          Cancel·lar
        </button>
      </div>
    </div>
  );
}

// Editor de franges horàries: es marquen els dies de la setmana als quals
// s'aplica una franja i s'introdueix una sola vegada l'hora d'inici/final;
// s'afegeix a tots els dies marcats de cop. A sota es veu clarament, dia a
// dia amb el nom complet, quines franges té cada dia o si està "Tancat".
export function TramsPerDiaEditor({ trams, onChange }) {
  const [diesSeleccionats, setDiesSeleccionats] = useState([]);
  const [horaInici, setHoraInici] = useState("");
  const [horaFinal, setHoraFinal] = useState("");
  const [avis, setAvis] = useState("");

  const tramsPerDia = {};
  DIES_SETMANA.forEach((d) => {
    tramsPerDia[d.valor] = [];
  });
  trams.forEach((t) => {
    const dia = Number(t.DiaSetmana);
    if (tramsPerDia[dia]) tramsPerDia[dia].push(t);
  });

  const toggleDia = (dia) => {
    setDiesSeleccionats((prev) => (prev.includes(dia) ? prev.filter((d) => d !== dia) : [...prev, dia]));
  };

  const afegirFranja = () => {
    if (diesSeleccionats.length === 0) {
      setAvis("Selecciona almenys un dia de la setmana.");
      return;
    }
    if (!horaInici || !horaFinal) {
      setAvis("Indica l'hora d'inici i final.");
      return;
    }
    const nous = diesSeleccionats.map((dia) => ({ DiaSetmana: dia, HoraInici: horaInici, HoraFinal: horaFinal }));
    onChange([...trams, ...nous]);
    setAvis("");
  };

  const treureFranja = (tramAEliminar) => {
    onChange(trams.filter((t) => t !== tramAEliminar));
  };

  return (
    <div className="horari-empresa-editor-franges">
      <div className="horari-empresa-afegir-franja">
        <span className="horari-empresa-selector-dies">
          {DIES_SETMANA.map((dia) => (
            <button
              key={dia.valor}
              type="button"
              title={dia.nom}
              className={`horari-empresa-dia-toggle${diesSeleccionats.includes(dia.valor) ? " horari-empresa-dia-toggle-actiu" : ""}`}
              onClick={() => toggleDia(dia.valor)}
            >
              {dia.curt}
            </button>
          ))}
        </span>
        <span className="horari-empresa-afegir-franja-hores">
          <input type="time" value={horaInici} onChange={(e) => setHoraInici(e.target.value)} />
          <span>–</span>
          <input type="time" value={horaFinal} onChange={(e) => setHoraFinal(e.target.value)} />
          <button type="button" className="dv2-btn dv2-btn--primary dv2-btn--compact horari-empresa-btn-petit" onClick={afegirFranja}>
            + Afegir als dies marcats
          </button>
        </span>
      </div>
      {avis && <p className="horari-empresa-avis">{avis}</p>}

      <div className="horari-empresa-franges-llista">
        {DIES_SETMANA.map((dia) => (
          <div key={dia.valor} className="horari-empresa-dia-fila">
            <div className="horari-empresa-dia-nom">{dia.nom}</div>
            <div className="horari-empresa-dia-franges">
              {tramsPerDia[dia.valor].length === 0 ? (
                <span className="horari-empresa-tancat">Tancat</span>
              ) : (
                tramsPerDia[dia.valor].map((tram, index) => (
                  <span key={index} className="horari-empresa-franja-chip">
                    {formatHoraCurta(tram.HoraInici)}–{formatHoraCurta(tram.HoraFinal)}
                    <button type="button" onClick={() => treureFranja(tram)}>✕</button>
                  </span>
                ))
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// Formulari complet per crear un Període nou: primer el rang de dates
// (calendari de tot l'any), i a sota les franges horàries.
export function FormNouPeriode({ idEmpresa, idHoraris, onCancel, onCreat }) {
  const [nomPeriode, setNomPeriode] = useState("");
  const [rang, setRang] = useState(null);
  const [trams, setTrams] = useState([]);
  const [enviant, setEnviant] = useState(false);
  const [errorForm, setErrorForm] = useState("");

  const handleCrear = async () => {
    setEnviant(true);
    setErrorForm("");
    try {
      const res = await fetchAutenticat(`${API_URL}/NouPeriodeHorari.php`, {
        method: "POST",
        body: JSON.stringify({
          idEmpresa,
          idHoraris,
          NomPeriode: nomPeriode || null,
          Rangs: [{ DataInici: rang.DataInici, DataFinal: rang.DataFinal, Motiu: null }],
          Trams: trams,
        }),
      });
      const data = await res.json();
      if (data.success) {
        onCreat(data.horari);
      } else {
        setErrorForm(data.message || "No s'ha pogut crear el Període.");
      }
    } catch (e) {
      if (!(e instanceof SessioCaducadaError)) setErrorForm("Error de connexió amb el servidor.");
    } finally {
      setEnviant(false);
    }
  };

  TeclaEscEnter(onCancel, rang ? handleCrear : null);

  if (!rang) {
    return (
      <div className="dv2-card horari-empresa-formulari">
        <div className="horari-empresa-bloc-titol">Pas 1: tria les dates del període</div>
        <SelectorRangDates
          onConfirmar={(inici, final) => setRang({ DataInici: inici, DataFinal: final })}
          onCancelar={onCancel}
        />
      </div>
    );
  }

  return (
    <div className="dv2-card horari-empresa-formulari">
      <div className="horari-empresa-calendari-resum">
        Període del <strong>{formatDate(rang.DataInici)}</strong> al <strong>{formatDate(rang.DataFinal)}</strong>{" "}
        <button type="button" className="horari-empresa-link-canviar" onClick={() => setRang(null)}>
          Canviar dates
        </button>
      </div>

      <div className="dv2-fields dv2-fields--sizes">
        <div className="dv2-field dv2-field--llarg">
          <label className="dv2-label" htmlFor="horari-nom-periode">Nom del període (opcional)</label>
          <input
            id="horari-nom-periode"
            type="text"
            className="dv2-input"
            value={nomPeriode}
            onChange={(e) => setNomPeriode(e.target.value)}
            maxLength="60"
          />
        </div>
      </div>

      <div className="horari-empresa-bloc-titol">Pas 2: franges horàries (opcional, es poden afegir després)</div>
      <TramsPerDiaEditor trams={trams} onChange={setTrams} />

      {errorForm && <p className="horari-empresa-error">{errorForm}</p>}

      <div className="horari-empresa-accions">
        <button type="button" className="dv2-btn dv2-btn--primary" onClick={handleCrear} disabled={enviant}>
          Crear període
        </button>
        <button type="button" className="dv2-btn" onClick={onCancel} disabled={enviant}>
          Cancel·lar
        </button>
      </div>
    </div>
  );
}

// Component principal: llista de Períodes d'un Horari, amb edició completa
// (mode normal) o només lectura/resum (readOnly=true, per exemple des de
// Consultar Empleat).
function HorariEditor({ idEmpresa, horari, onHorariActualitzat, onRecarregar, readOnly = false }) {
  const [creantPeriode, setCreantPeriode] = useState(false);
  const [editantTramsPeriode, setEditantTramsPeriode] = useState(null);
  const [tramsEnEdicio, setTramsEnEdicio] = useState([]);
  const [desantTrams, setDesantTrams] = useState(false);
  const [errorTrams, setErrorTrams] = useState("");
  const [afegintRangPeriode, setAfegintRangPeriode] = useState(null);
  const [editantRang, setEditantRang] = useState(null);
  const [renombrantPeriode, setRenombrantPeriode] = useState(null);
  const [nouNomPeriode, setNouNomPeriode] = useState("");

  const tancarFormularis = () => {
    setCreantPeriode(false);
    setEditantTramsPeriode(null);
    setAfegintRangPeriode(null);
    setEditantRang(null);
    setRenombrantPeriode(null);
  };

  const aplicarHorari = (nouHorari) => {
    tancarFormularis();
    onHorariActualitzat?.(nouHorari);
  };

  const handleEsborrarPeriode = async (idHorarisPeriodes) => {
    if (!window.confirm("Segur que vols esborrar aquest Període (i tots els seus Rangs i Trams)?")) return;
    try {
      const res = await fetchAutenticat(`${API_URL}/EsborrarPeriodeHorari.php`, {
        method: "POST",
        body: JSON.stringify({ idEmpresa, idHorarisPeriodes }),
      });
      const data = await res.json();
      if (data.success) {
        aplicarHorari(data.horari);
      } else {
        alert(data.message || "No s'ha pogut esborrar el Període.");
      }
    } catch (e) {
      if (!(e instanceof SessioCaducadaError)) alert("Error de connexió amb el servidor.");
    }
  };

  const handleEsborrarRang = async (idHorarisPeriodesRangs) => {
    if (!window.confirm("Segur que vols esborrar aquest Rang de dates? Si el Període es queda sense cap Rang, també s'esborrarà.")) return;
    try {
      const res = await fetchAutenticat(`${API_URL}/EsborrarRangPeriode.php`, {
        method: "POST",
        body: JSON.stringify({ idEmpresa, idHorarisPeriodesRangs }),
      });
      const data = await res.json();
      if (data.success) {
        aplicarHorari(data.horari);
      } else {
        alert(data.message || "No s'ha pogut esborrar el Rang. Pot ser que ja s'hagi modificat des d'una altra acció; es recarregarà l'Horari.");
        onRecarregar?.();
      }
    } catch (e) {
      if (!(e instanceof SessioCaducadaError)) alert("Error de connexió amb el servidor.");
    }
  };

  const obrirEdicioTrams = (periode) => {
    setEditantTramsPeriode(periode.idHorarisPeriodes);
    setTramsEnEdicio((periode.Trams || []).map((t) => ({ DiaSetmana: t.DiaSetmana, HoraInici: t.HoraInici, HoraFinal: t.HoraFinal })));
    setErrorTrams("");
  };

  const handleDesarTrams = async (idHorarisPeriodes) => {
    setDesantTrams(true);
    setErrorTrams("");
    try {
      const res = await fetchAutenticat(`${API_URL}/ModificarPeriodeHorari.php`, {
        method: "POST",
        body: JSON.stringify({ idEmpresa, idHorarisPeriodes, Trams: tramsEnEdicio }),
      });
      const data = await res.json();
      if (data.success) {
        aplicarHorari(data.horari);
      } else {
        setErrorTrams(data.message || "No s'han pogut desar les franges.");
      }
    } catch (e) {
      if (!(e instanceof SessioCaducadaError)) setErrorTrams("Error de connexió amb el servidor.");
    } finally {
      setDesantTrams(false);
    }
  };

  const handleDesarRang = async (context, dataInici, dataFinal) => {
    try {
      const esNou = !context.idHorarisPeriodesRangs;
      const url = esNou ? `${API_URL}/NouRangPeriode.php` : `${API_URL}/ModificarRangPeriode.php`;
      const body = esNou
        ? { idEmpresa, idHorarisPeriodes: context.idHorarisPeriodes, DataInici: dataInici, DataFinal: dataFinal, Motiu: null }
        : { idEmpresa, idHorarisPeriodesRangs: context.idHorarisPeriodesRangs, DataInici: dataInici, DataFinal: dataFinal, Motiu: null };

      const res = await fetchAutenticat(url, {
        method: "POST",
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (data.success) {
        aplicarHorari(data.horari);
      } else {
        alert(data.message || "No s'han pogut desar les dates del Rang.");
        if ((data.message || "").toLowerCase().includes("no pertany")) {
          onRecarregar?.();
        }
      }
    } catch (e) {
      if (!(e instanceof SessioCaducadaError)) alert("Error de connexió amb el servidor.");
    }
  };

  const handleRenombrar = async (idHorarisPeriodes) => {
    try {
      const res = await fetchAutenticat(`${API_URL}/ModificarPeriodeHorari.php`, {
        method: "POST",
        body: JSON.stringify({ idEmpresa, idHorarisPeriodes, NomPeriode: nouNomPeriode || null }),
      });
      const data = await res.json();
      if (data.success) {
        aplicarHorari(data.horari);
      } else {
        alert(data.message || "No s'ha pogut canviar el nom del Període.");
      }
    } catch (e) {
      if (!(e instanceof SessioCaducadaError)) alert("Error de connexió amb el servidor.");
    }
  };

  TeclaEscEnter(
    () => {
      if (renombrantPeriode) setRenombrantPeriode(null);
      if (editantTramsPeriode) setEditantTramsPeriode(null);
    },
    () => {
      if (renombrantPeriode) handleRenombrar(renombrantPeriode);
      else if (editantTramsPeriode) handleDesarTrams(editantTramsPeriode);
    }
  );

  if (!horari) return null;

  return (
    <>
      {!readOnly && (
        <div className="horari-empresa-accions">
          <button type="button" className="dv2-btn dv2-btn--primary" onClick={() => setCreantPeriode(true)}>
            + Afegir període
          </button>
        </div>
      )}

      {!readOnly && creantPeriode && (
        <FormNouPeriode
          idEmpresa={idEmpresa}
          idHoraris={horari.idHoraris}
          onCancel={() => setCreantPeriode(false)}
          onCreat={(nouHorari) => aplicarHorari(nouHorari)}
        />
      )}

      {(horari.Periodes || []).length === 0 && !creantPeriode && (
        <p className="dv2-hint">Aquest Horari encara no té cap Període definit.</p>
      )}

      <div className="horari-empresa-periodes">
        {(horari.Periodes || []).map((periode) => (
          <div className="dv2-card horari-empresa-periode" key={periode.idHorarisPeriodes}>
            <div className="horari-empresa-periode-header">
              {!readOnly && renombrantPeriode === periode.idHorarisPeriodes ? (
                <span className="horari-empresa-renombrar">
                  <input
                    type="text"
                    className="dv2-input"
                    value={nouNomPeriode}
                    maxLength="60"
                    onChange={(e) => setNouNomPeriode(e.target.value)}
                    autoFocus
                  />
                  <button type="button" className="dv2-btn dv2-btn--icon dv2-btn--ghost" title="Desar nom" onClick={() => handleRenombrar(periode.idHorarisPeriodes)}>✓</button>
                  <button type="button" className="dv2-btn dv2-btn--icon dv2-btn--ghost" title="Cancel·lar" onClick={() => setRenombrantPeriode(null)}>✕</button>
                </span>
              ) : (
                <strong
                  className="horari-empresa-nom-periode"
                  title={readOnly ? undefined : "Fes clic per canviar el nom"}
                  onClick={
                    readOnly
                      ? undefined
                      : () => {
                          setRenombrantPeriode(periode.idHorarisPeriodes);
                          setNouNomPeriode(periode.NomPeriode || "");
                        }
                  }
                >
                  {periode.NomPeriode || `Període #${periode.idHorarisPeriodes}`} {!readOnly && "✎"}
                </strong>
              )}
              {!readOnly && (
                <button
                  type="button"
                  className="dv2-btn dv2-btn--icon dv2-btn--ghost"
                  title="Esborrar període"
                  onClick={() => handleEsborrarPeriode(periode.idHorarisPeriodes)}
                >
                  🗑️
                </button>
              )}
            </div>

            <div className="horari-empresa-bloc">
              <div className="horari-empresa-bloc-titol">Dates del període</div>
              <div className="horari-empresa-rangs-chips">
                {(periode.Rangs || []).map((rang) => (
                  <span className="horari-empresa-rang-chip" key={rang.idHorarisPeriodesRangs}>
                    {formatDate(rang.DataInici)} — {formatDate(rang.DataFinal)}
                    {!readOnly && (
                      <>
                        <button
                          type="button"
                          title="Canviar aquestes dates"
                          onClick={() =>
                            setEditantRang({
                              idHorarisPeriodesRangs: rang.idHorarisPeriodesRangs,
                              idHorarisPeriodes: periode.idHorarisPeriodes,
                              DataInici: rang.DataInici,
                              DataFinal: rang.DataFinal,
                            })
                          }
                        >
                          ✎
                        </button>
                        <button type="button" title="Esborrar aquest rang" onClick={() => handleEsborrarRang(rang.idHorarisPeriodesRangs)}>
                          ✕
                        </button>
                      </>
                    )}
                  </span>
                ))}
                {!readOnly && afegintRangPeriode !== periode.idHorarisPeriodes && editantRang?.idHorarisPeriodes !== periode.idHorarisPeriodes && (
                  <button
                    type="button"
                    className="dv2-btn dv2-btn--primary dv2-btn--compact horari-empresa-btn-petit"
                    onClick={() => setAfegintRangPeriode(periode.idHorarisPeriodes)}
                  >
                    + Afegir rang
                  </button>
                )}
              </div>

              {!readOnly && afegintRangPeriode === periode.idHorarisPeriodes && (
                <SelectorRangDates
                  onConfirmar={(inici, final) =>
                    handleDesarRang({ idHorarisPeriodes: periode.idHorarisPeriodes, idHorarisPeriodesRangs: null }, inici, final)
                  }
                  onCancelar={() => setAfegintRangPeriode(null)}
                />
              )}

              {!readOnly && editantRang?.idHorarisPeriodes === periode.idHorarisPeriodes && editantRang?.idHorarisPeriodesRangs && (
                <SelectorRangDates
                  dataIniciInicial={editantRang.DataInici}
                  dataFinalInicial={editantRang.DataFinal}
                  onConfirmar={(inici, final) =>
                    handleDesarRang(
                      { idHorarisPeriodes: editantRang.idHorarisPeriodes, idHorarisPeriodesRangs: editantRang.idHorarisPeriodesRangs },
                      inici,
                      final
                    )
                  }
                  onCancelar={() => setEditantRang(null)}
                />
              )}
            </div>

            <div className="horari-empresa-bloc">
              <div className="horari-empresa-bloc-titol">Franges horàries</div>
              {!readOnly && editantTramsPeriode === periode.idHorarisPeriodes ? (
                <>
                  <TramsPerDiaEditor trams={tramsEnEdicio} onChange={setTramsEnEdicio} />
                  {errorTrams && <p className="horari-empresa-error">{errorTrams}</p>}
                  <div className="horari-empresa-accions">
                    <button
                      type="button"
                      className="dv2-btn dv2-btn--primary"
                      disabled={desantTrams}
                      onClick={() => handleDesarTrams(periode.idHorarisPeriodes)}
                    >
                      Desar franges
                    </button>
                    <button
                      type="button"
                      className="dv2-btn"
                      disabled={desantTrams}
                      onClick={() => setEditantTramsPeriode(null)}
                    >
                      Cancel·lar
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <div className="horari-empresa-franges-resum">
                    {DIES_SETMANA.map((dia) => {
                      const tramsDia = (periode.Trams || []).filter((t) => Number(t.DiaSetmana) === dia.valor);
                      return (
                        <div key={dia.valor} className="horari-empresa-franges-resum-fila">
                          <strong>{dia.nom}:</strong> {formatTramsDia(tramsDia)}
                        </div>
                      );
                    })}
                  </div>
                  {!readOnly && (
                    <button
                      type="button"
                      className="dv2-btn dv2-btn--primary dv2-btn--compact horari-empresa-btn-petit"
                      onClick={() => obrirEdicioTrams(periode)}
                    >
                      Editar franges
                    </button>
                  )}
                </>
              )}
            </div>
          </div>
        ))}
      </div>
    </>
  );
}

// Resum en text pla d'un Horari (per a pantalles de "Consultar", on no volem
// mostrar l'estructura tècnica de Períodes/Rangs/Trams sinó frases
// llegibles). Retorna un array (un element per Període, ordenats pel primer
// Rang) amb { etiqueta, rangsText, grups: [{ diesText, horesText }] }.
// Els dies tancats (sense Trams) no es mostren.
export function resumHorariText(horari) {
  const periodes = horari?.Periodes || [];
  if (periodes.length === 0) return [];

  const primerInici = (periode) => {
    const dates = (periode.Rangs || []).map((r) => new Date(r.DataInici).getTime());
    return dates.length > 0 ? Math.min(...dates) : Infinity;
  };

  const periodesOrdenats = [...periodes].sort((a, b) => primerInici(a) - primerInici(b));

  return periodesOrdenats.map((periode, index) => {
    const rangsText = unirAmbI(
      (periode.Rangs || []).map((r) => `${formatDate(r.DataInici)} a ${formatDate(r.DataFinal)}`)
    );

    const signaturaDia = (diaValor) =>
      (periode.Trams || [])
        .filter((t) => Number(t.DiaSetmana) === diaValor)
        .map((t) => `${t.HoraInici}-${t.HoraFinal}`)
        .sort()
        .join("|");

    const grups = [];
    let grupActual = null;
    DIES_SETMANA.forEach((dia) => {
      const signatura = signaturaDia(dia.valor);
      if (!signatura) {
        grupActual = null; // dia tancat: no es mostra al resum
        return;
      }
      if (grupActual && grupActual.signatura === signatura) {
        grupActual.diaFi = dia;
      } else {
        grupActual = { signatura, diaInici: dia, diaFi: dia };
        grups.push(grupActual);
      }
    });

    const grupsText = grups.map((g) => {
      const diesText = g.diaInici.valor === g.diaFi.valor ? g.diaInici.nom : `${g.diaInici.nom} a ${g.diaFi.nom}`;
      const tramsDia = (periode.Trams || []).filter((t) => Number(t.DiaSetmana) === g.diaInici.valor);
      return { diesText, horesText: formatTramsDia(tramsDia) };
    });

    return {
      etiqueta: `Període ${index + 1}`,
      rangsText: rangsText || "—",
      grups: grupsText,
    };
  });
}

export default HorariEditor;
